import { Popover } from 'antd';
import type { TooltipPlacement } from 'antd/es/tooltip';
import type { Dayjs } from 'dayjs';
import type { ReactNode } from 'react';

import { AvailabilityForm } from '../AvailabilityForm/AvailabilityForm';
import type {
  AvailabilityFormValues,
  AvailabilityInitialValues,
} from '../AvailabilityForm/availabilityForm.types';

import styles from './AvailabilityPopover.module.scss';
import { useAvailabilityPopoverGroup } from './availabilityPopoverContext';

type PopoverState = {
  open: boolean;
};

type Props = {
  id: string;
  children: ReactNode | ((state: PopoverState) => ReactNode);
  title: string;
  subtitle?: string;
  placement?: TooltipPlacement;
  initialValues: AvailabilityInitialValues;
  durationMinutes?: number;
  onSubmit: (values: AvailabilityFormValues) => void;
  onDelete?: () => void;
  onChange?: (values: AvailabilityFormValues | null) => void;
  isWithinOpeningHours?: (start: Dayjs) => boolean;
  isDayClosed?: (date: Dayjs) => boolean;
  isEditPopover?: boolean;
};

export const AvailabilityPopover = ({
  id,
  children,
  title,
  subtitle,
  placement = 'right',
  initialValues,
  durationMinutes,
  onSubmit,
  onDelete,
  onChange,
  isWithinOpeningHours,
  isDayClosed,
  isEditPopover = false,
}: Props) => {
  const { activeId, setActiveId } = useAvailabilityPopoverGroup();

  const open = activeId === id;

  const close = () => setActiveId(null);

  const handleOpenChange = (next: boolean) => {
    if (next) {
      setActiveId(id);
    }
  };

  const handleSubmit = (values: AvailabilityFormValues) => {
    onSubmit(values);
    close();
  };

  const handleDelete = onDelete
    ? () => {
        onDelete();
        close();
      }
    : undefined;

  return (
    <Popover
      open={open}
      onOpenChange={handleOpenChange}
      trigger="click"
      placement={placement}
      destroyTooltipOnHide
      overlayClassName={styles.overlay}
      content={
        <AvailabilityForm
          title={title}
          subtitle={subtitle}
          initialValues={initialValues}
          durationMinutes={durationMinutes}
          onSubmit={handleSubmit}
          onClose={close}
          onDelete={handleDelete}
          onChange={onChange}
          isWithinOpeningHours={isWithinOpeningHours}
          isDayClosed={isDayClosed}
          isEditPopover={isEditPopover}
        />
      }
    >
      <div
        className={styles.trigger}
        onClick={(e) => e.stopPropagation()}
        role="button"
        tabIndex={0}
      >
        {typeof children === 'function' ? children({ open }) : children}
      </div>
    </Popover>
  );
};
